import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, ChevronDown, Waves, UserPlus } from "lucide-react";

const solutionItems = [
  { slug: "floating-infrastructure-solutions", label: "Floating Infrastructure Solutions" },
  { slug: "marine-navigation-safety-solutions", label: "Marine Navigation & Safety Solutions" },
  { slug: "marine-access-berthing-solutions", label: "Marine Access & Berthing Solutions" },
  { slug: "coastal-waterfront-solutions", label: "Coastal & Waterfront Solutions" },
  { slug: "marine-coating-solutions", label: "Marine Infrastructure Coating Solutions" },
  { slug: "marine-vessels-equipment-solutions", label: "Marine Vessels & Equipment Solutions" },
];

const primaryLinks = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/solutions", label: "Our Solutions", dropdown: true },
  { to: "/contact", label: "Contact Us" },
];

// Compact MIA logo mark (stylized figure standing on waves)
export const MiaLogoMark = ({ className = "w-10 h-10" }) => (
  <svg viewBox="0 0 64 64" className={className} aria-hidden="true">
    <defs>
      <linearGradient id="mia-logo-g" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#1B62B8" />
        <stop offset="1" stopColor="#0B2545" />
      </linearGradient>
    </defs>
    <circle cx="32" cy="32" r="30" fill="url(#mia-logo-g)" opacity="0.08" />
    <circle cx="32" cy="18" r="6" fill="#0B2545" />
    <path d="M20 40 C24 32 40 32 44 40 L44 46 L20 46 Z" fill="#0B2545" />
    <path d="M6 50 Q16 44 26 50 T46 50 T64 50" fill="none" stroke="#1B62B8" strokeWidth="2.5" strokeLinecap="round" />
    <path d="M2 56 Q14 50 26 56 T50 56 T64 56" fill="none" stroke="#1B62B8" strokeWidth="2" strokeLinecap="round" opacity="0.6" />
  </svg>
);

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [ddOpen, setDdOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setDdOpen(false);
  }, [loc.pathname]);

  return (
    <header className={`w-full sticky top-0 z-50 bg-white transition-shadow ${scrolled ? "shadow-md" : "shadow-[0_1px_0_rgba(11,37,69,0.06)]"}`}>
      <div className="mia-container py-3 sm:py-4 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center min-w-0" data-testid="nav-logo">
          <img
            src="https://customer-assets-lxgj4vgw.emergentagent.net/job_mia-waterfront/artifacts/abj03nmq_MARINE%20INFRASTRUCTURE%20LOGO.webp"
            alt="Marine Infrastructure Africa"
            className="h-5 sm:h-6 lg:h-8 w-auto max-w-[55vw] sm:max-w-[240px] lg:max-w-[280px] shrink-0"
          />
        </Link>

        <nav className="hidden lg:flex items-center mr-4 xl:mr-6">
          {primaryLinks.map((l) => (
            <div
              key={l.to}
              className="relative"
              onMouseEnter={() => l.dropdown && setDdOpen(true)}
              onMouseLeave={() => l.dropdown && setDdOpen(false)}
            >
              <NavLink
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 xl:px-4 py-3 text-[0.72rem] xl:text-[0.78rem] font-bold tracking-[0.08em] xl:tracking-[0.1em] uppercase inline-flex items-center gap-1 transition-colors relative whitespace-nowrap ${
                    isActive
                      ? "text-[color:var(--mia-navy)] after:content-[''] after:absolute after:left-3 xl:after:left-4 after:right-3 xl:after:right-4 after:bottom-0 after:h-[3px] after:bg-[color:var(--mia-navy)]"
                      : "text-[color:var(--mia-muted)] hover:text-[color:var(--mia-navy)]"
                  }`
                }
              >
                {l.label}
                {l.dropdown && <ChevronDown className="w-3.5 h-3.5" />}
              </NavLink>

              {l.dropdown && ddOpen && (
                <div className="absolute top-full left-0 min-w-[340px] max-h-[75vh] overflow-y-auto bg-white shadow-2xl border border-slate-100 rounded-md z-40">
                  {solutionItems.map((s) => (
                    <Link
                      key={s.slug}
                      to={`/solutions/${s.slug}`}
                      className="block px-5 py-3 text-sm text-[color:var(--mia-navy)] hover:bg-[color:var(--mia-navy)] hover:text-white transition-colors"
                      data-testid={`dropdown-${s.slug}`}
                    >
                      {s.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <Link
            to="/careers"
            className="hidden lg:inline-flex items-center gap-1.5 text-[0.72rem] xl:text-[0.78rem] font-bold tracking-[0.08em] xl:tracking-[0.1em] uppercase text-[color:var(--mia-navy)] hover:text-[color:var(--mia-green)] transition-colors whitespace-nowrap"
            data-testid="nav-join-team"
          >
            <UserPlus className="w-4 h-4" /> Join Our Team
          </Link>
          <Link to="/request-consultation" className="mia-btn mia-btn-navy !hidden lg:!inline-flex text-[0.72rem] xl:text-[0.78rem] px-4 xl:px-5" data-testid="nav-cta-consultation">
            Request Consultation
          </Link>
          <button
            className="lg:hidden w-11 h-11 rounded-md border border-slate-200 flex items-center justify-center"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-slate-100 bg-white" data-testid="mobile-menu">
          <div className="mia-container py-6 space-y-3">
            {primaryLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className="block text-base font-bold text-[color:var(--mia-navy)] uppercase tracking-wider"
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </NavLink>
            ))}
            <NavLink
              to="/careers"
              className="block text-base font-bold text-[color:var(--mia-navy)] uppercase tracking-wider"
              data-testid="mobile-nav-careers"
            >
              Join Our Team
            </NavLink>
            <Link to="/request-consultation" className="mia-btn mia-btn-navy w-full mt-4" data-testid="mobile-nav-cta">
              Request Consultation
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
